// The loaded composer CONSOLE (prototype composerView over the fetched projection): the crumb row
// with the operator pill and the honest draft/live indicator, the step rail beside the build column,
// and — after the Activate press — EITHER the success state (only when the owning landing read
// confirms the workflow live, G-182) or the honest unconfirmed note (never an optimistic "live",
// P-SELF). All view state lives in useComposerState; every configuration value renders from the
// projection. Navigation leaves ONLY via the injected callbacks (surface-contracts: callbacks-only,
// shell-agnostic); the console routes no verdict write and sets no approval field (P-GATE).

import { Dot } from '../../../primitives.tsx';
import type { WorkComposerProjection } from '../../projections.ts';
import { composerStepAt } from '../composer-model.ts';
import { GHOST_BUTTON_CLASS, ShieldIcon } from '../composer-ui.tsx';
import { useComposerState, type ComposerStateApi } from '../use-composer-state.ts';
import { ActivatedView } from './ActivatedView.tsx';
import { DraftingReveal } from './DraftingReveal.tsx';
import { StepBodySwitch, StepFoot, StepHead } from './step-frame.tsx';
import { StepRail } from './StepRail.tsx';

function ComposerCrumb({
  drafting,
  onBack,
}: {
  readonly drafting: boolean;
  readonly onBack: () => void;
}): React.JSX.Element {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-2 text-sm text-text-muted">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to workflows"
          className={GHOST_BUTTON_CLASS}
        >
          Workflows
        </button>
        <span aria-hidden="true">/</span>
        <span className="font-medium text-text">Compose</span>
        <span className="ritona-numeric inline-flex items-center gap-1.5 rounded-sm border border-bracken bg-bracken-tint px-2 py-0.5 text-xs uppercase tracking-eyebrow text-bracken">
          <ShieldIcon size={11} />
          Operator console
        </span>
      </div>
      <span
        role="status"
        className="ritona-numeric inline-flex items-center gap-1.5 text-xs text-text-muted"
      >
        <Dot tone={drafting ? 'go' : 'neutral'} />
        {drafting ? 'Ritona drafting' : 'Draft · not live'}
      </span>
    </div>
  );
}

/** The build column: the open step's head, its body (or the drafting reveal), and the review foot. */
function BuildColumn({
  config,
  state,
  onBack,
}: {
  readonly config: WorkComposerProjection;
  readonly state: ComposerStateApi;
  readonly onBack: () => void;
}): React.JSX.Element {
  const step = composerStepAt(state.stepIndex);
  return (
    <div className="min-w-0 max-w-3xl">
      <StepHead step={step} workflowName={config.workflowName} />
      {state.drafting ? (
        <DraftingReveal onDone={state.finishDraft} />
      ) : (
        <StepBodySwitch
          step={step}
          config={config}
          activeView={state.activeView}
          busy={state.busy}
          onDraft={state.startDraft}
          onPickView={state.pickView}
          onActivate={state.activate}
          onSaveDraft={onBack}
        />
      )}
      {!state.drafting && !step.gate ? (
        <StepFoot
          stepIndex={state.stepIndex}
          primaryNext={step.id !== 'brief'}
          onBack={state.back}
          onNext={state.next}
        />
      ) : null}
    </div>
  );
}

/** The honest post-press state when the owning read did NOT confirm the workflow live: no seal, no
 *  "live" claim, no fabricated board link — a plain status and the way back (P-SELF, G-182). */
function ActivationUnconfirmed({ onBack }: { readonly onBack: () => void }): React.JSX.Element {
  return (
    <div data-activation="unconfirmed" className="mx-auto mt-16 max-w-xl text-center">
      <p role="status" className="mx-auto max-w-prose text-sm leading-relaxed text-text-muted">
        Activation could not be confirmed — the workflows list does not show this workflow live, and
        nothing has been recorded on your behalf. This console stays review-only for now; your
        Ritona engineer completes each activation with you.
      </p>
      <div className="mt-6 flex items-center justify-center">
        <button
          type="button"
          aria-label="Back to workflows"
          onClick={onBack}
          className={GHOST_BUTTON_CLASS}
        >
          Back to workflows
        </button>
      </div>
    </div>
  );
}

export interface ComposerLoadedProps {
  readonly config: WorkComposerProjection;
  readonly onBack: () => void;
  readonly onOpenBoard: (definitionId: string) => void;
}

/** The interactive console over the loaded projection. */
export function ComposerLoaded({
  config,
  onBack,
  onOpenBoard,
}: ComposerLoadedProps): React.JSX.Element {
  const state = useComposerState(config);
  if (state.activated !== null) {
    if (!state.activated.confirmed) {
      return <ActivationUnconfirmed onBack={onBack} />;
    }
    return (
      <ActivatedView
        workflowName={config.workflowName}
        cadenceLabel={config.cadenceLabel}
        timezone={config.timezone}
        boardId={state.activated.boardId}
        onOpenBoard={onOpenBoard}
        onBack={onBack}
      />
    );
  }
  return (
    <div>
      <ComposerCrumb drafting={state.drafting} onBack={onBack} />
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[15rem_1fr]">
        <StepRail
          workflowName={config.workflowName}
          currentIndex={state.stepIndex}
          maxReached={state.maxReached}
          onSelect={state.goToStep}
        />
        <BuildColumn config={config} state={state} onBack={onBack} />
      </div>
    </div>
  );
}
